import { Body, Button, Heading, IconButton } from '@assembly-js/design-system';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

import crmPreviewMobile from './assets/crm-preview-mobile.png';
import crmPreview from './assets/crm-preview.png';

const DESKTOP_WIDTH_PX = 764;
const DESKTOP_HEIGHT_PX = 414;

interface CrmPreviewModalProps {
  isOpen: boolean;
  /** Dismiss — only the close button (X) or an explicit CTA, never the backdrop. */
  onClose: () => void;
  title: string;
  description: string;
  /** Accessible label for the close button. */
  dismissLabel: string;
  /** Alt text for the CRM preview image. */
  previewAlt: string;
  /** Primary CTA — label + handler. */
  primaryLabel: string;
  onPrimary: () => void;
  /** Optional secondary text button (e.g. "Open sign-in page"). */
  secondaryLabel?: string;
  onSecondary?: () => void;
  /**
   * When true the secondary button is shown only on mobile (hidden on desktop,
   * where the close button suffices); otherwise it shows at all breakpoints.
   */
  secondaryMobileOnly?: boolean;
}

/**
 * Studio FTUX modal shell: a message with CTAs laid beside a CRM preview image.
 * Desktop lays the copy beside the preview; mobile stacks the copy over a
 * full-height preview takeover. Shared by the post-publish "app is live" modal
 * and the "before you enter the portal" intercept — copy and actions differ,
 * the two-panel frame does not.
 */
export const CrmPreviewModal = ({
  isOpen,
  onClose,
  title,
  description,
  dismissLabel,
  previewAlt,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  secondaryMobileOnly = false,
}: CrmPreviewModalProps) => {
  const isMobile = useMediaQuery('sm', 'max');
  const secondaryVisible = !secondaryMobileOnly || isMobile;

  const handleWrapperClose: ModalWrapperProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  return (
    <ModalWrapper
      open={isOpen}
      onClose={handleWrapperClose}
      hideActions
      hideDividers
      disableGutters
      fullWidth={isMobile}
      width={isMobile ? undefined : DESKTOP_WIDTH_PX}
      height={isMobile ? '100%' : DESKTOP_HEIGHT_PX}
      sx={
        isMobile
          ? {
              '& .MuiDialog-paper': {
                m: 0,
                maxWidth: '100%',
                maxHeight: '100%',
                border: 'none',
                borderRadius: 0,
                height: '100%',
              },
            }
          : undefined
      }
      aria-label={title}
    >
      <div className="flex h-full w-full flex-col bg-white sm:w-auto sm:flex-row">
        <IconButton
          icon="Close"
          variant={isMobile ? 'minimal' : 'secondary'}
          aria-label={dismissLabel}
          onClick={onClose}
          className="absolute z-10 right-4 top-4 sm:right-2 sm:top-2 sm:!rounded-full"
        />

        <div className="flex flex-col gap-4 p-5 sm:flex-1 sm:gap-6 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-1">
            <Heading
              tag="h2"
              size="xl"
              className="!m-0 pr-6 !leading-7 text-black-heading sm:pr-0"
            >
              {title}
            </Heading>
            <Body size="sm" className="!m-0 !leading-[21px] text-gray-small">
              {description}
            </Body>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              autoFocus
              label={primaryLabel}
              onClick={onPrimary}
            />
            {secondaryVisible && secondaryLabel && onSecondary && (
              <Button
                variant="text"
                size="sm"
                label={secondaryLabel}
                onClick={onSecondary}
              />
            )}
          </div>
        </div>

        {/* Each preview asset bakes in its own gray mat, 20px top/left inset, and
            bleed. Desktop uses the landscape asset covering its fixed 380px panel;
            mobile is an immersive full-height takeover, so it uses a taller portrait
            asset that covers the flex-filled panel and bleeds off the bottom. Because
            each asset's ratio matches its panel, object-cover only trims the intended
            bleed rather than cropping CRM content. */}
        <div className="relative w-full flex-1 overflow-hidden bg-gray-150 sm:max-w-[380px] sm:flex-initial">
          <img
            src={isMobile ? crmPreviewMobile : crmPreview}
            decoding="async"
            alt={previewAlt}
            className="absolute inset-0 h-full w-full object-cover object-top sm:object-left-top"
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
